// GroundwaterForm.jsx
import { useState } from "react"
import { UniversalDropDown } from "@fileops/swmm-react"
import { Button } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Groundwater Form is a group of data inputs usually shown when clicking on Subcatchment->Groundwater. 
 * This form is contained within GroundwaterDialog.
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @param {string} target The name of the node targetted by this form.
 * @param {function} setOpenGWF Function to set flag for opening a GWF dialog.
 * @returns UI for interactive swmm modeling.
 */
export default function GroundwaterForm({model, setModel, fullModel, target, setOpenGWF}) {
  const [aquiferName, setAquiferName]           = useState(model.GROUNDWATER[target].Aquifer?model.GROUNDWATER[target].Aquifer:'')
  const [receivingNode, setReceivingNode]       = useState(model.GROUNDWATER[target].Node)
  const [surfaceElevation, setSurfaceElevation] = useState(model.GROUNDWATER[target].Esurf)
  const [A1, setA1]                             = useState(model.GROUNDWATER[target].A1)
  const [B1, setB1]                             = useState(model.GROUNDWATER[target].B1)
  const [A2, setA2]                             = useState(model.GROUNDWATER[target].A2)
  const [B2, setB2]                             = useState(model.GROUNDWATER[target].B2)
  const [A3, setA3]                             = useState(model.GROUNDWATER[target].A3)
  const [dsw, setDsw]                           = useState(model.GROUNDWATER[target].Dsw)
  const [egwt, setEgwt]                         = useState(model.GROUNDWATER[target].Egwt)
  const [ebot, setEbot]                         = useState(model.GROUNDWATER[target].Ebot)
  const [egw, setEgw]                           = useState(model.GROUNDWATER[target].Egw)
  const [umc, setUmc]                           = useState(model.GROUNDWATER[target].Umc)
  const [aquiferIDs, setAquiferIDs]   = useState((Object.keys(fullModel.AQUIFERS).length===0?[]:Object.keys(fullModel.AQUIFERS)))

  const updateValue = (val, type) => {
    if(model && model.GROUNDWATER){
      setModel(prev => ({
        ...prev,
        GROUNDWATER:{
          ...prev.GROUNDWATER,
          [target]: {
            ...prev.GROUNDWATER[target],
            [type]: val
          }
        }
      }))
    }
  }

  if(model){
    return(
      <>
      {
        <>
          {(
            target && 
            target.length > 0 && 
            <>
            <label className='editor-small' >Aquifer Name
              <Button style={{padding: '0px', float:'right', display:'inline' }} color={'error'} onClick={()=>{setAquiferName('*'); updateValue('*', 'Aquifer')}}>CLEAR</Button>
              <UniversalDropDown style={{padding: '0'}}
                IDs={aquiferIDs} 
                selectText={"Select an Aquifer"} 
                value={aquiferName}
                onChange={(val)=>{setAquiferName(val);updateValue(val, 'Aquifer')}} >
              </UniversalDropDown>
            </label>
            <label className='editor-small' >Receiving Node
              <input  type = "text"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setReceivingNode(e.target.value); updateValue(e.target.value, 'Node')}}
                      value={receivingNode}
                      id={'ReceivingNode'+target}
              />
            </label>
            <label className='editor-small' >Surface Elevation
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setSurfaceElevation(e.target.value); updateValue(e.target.value, 'Esurf')}}
                      value={surfaceElevation}
                      id={'SurfaceElevation'+target}
              />
            </label>
            <label className='editor-small' >A1 Coefficient
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setA1(e.target.value); updateValue(e.target.value, 'A1')}}
                      value={A1}
                      id={'A1'+target}
              />
            </label>
            <label className='editor-small' >B1 Exponent
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setB1(e.target.value); updateValue(e.target.value, 'B1')}}
                      value={B1}
                      id={'B1'+target}
              />
            </label>
            <label className='editor-small' >A2 Coefficient
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setA2(e.target.value); updateValue(e.target.value, 'A2')}}
                      value={A2}
                      id={'A2'+target}
              />
            </label>
            <label className='editor-small' >B2 Exponent
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setB2(e.target.value); updateValue(e.target.value, 'B2')}}
                      value={B2}
                      id={'B2'+target}
              />
            </label>
            <label className='editor-small' >A3 Coefficient
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setA3(e.target.value); updateValue(e.target.value, 'A3')}}
                      value={A3}
                      id={'A3'+target}
              />
            </label>
            <label className='editor-small' >Surface Water Depth
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setDsw(e.target.value); updateValue(e.target.value, 'Dsw')}}
                      value={dsw}
                      id={'dsw'+target}
              />
            </label>
            <label className='editor-small' >Threshold Water Table Elev.
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setEgwt(e.target.value); updateValue(e.target.value, 'Egwt')}}
                      value={egwt}
                      id={'egwt'+target}
              />
            </label>
            <label className='editor-small' >Aquifer Bottom Elevation
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setEbot(e.target.value); updateValue(e.target.value, 'Ebot')}}
                      value={ebot}
                      id={'ebot'+target}
              />
            </label>
            <label className='editor-small' >Initial Water Table Elevation
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setEgw(e.target.value); updateValue(e.target.value, 'Egw')}}
                      value={egw}
                      id={'egw'+target}
              />
            </label>
            <label className='editor-small' >Unsat Zone Moisture
              <input  type = "number"
                      style={{width: '95%', display: 'flex'}}
                      onChange={(e)=>{setUmc(e.target.value); updateValue(e.target.value, 'Umc')}}
                      value={umc}
                      id={'umc'+target}
              />
            </label>
            {
              <Button onClick={()=>setOpenGWF(true)}>GWF</Button>
            }
            </>
          )}
        </>
      }
      </>
    )
  } else {
    return null
  }
}